import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { formatIst, inboxApi, templateButtons, type Template } from '@wa/shared';

/**
 * "Send WhatsApp Template" — the reference's picker, which is the whole send
 * flow for a broadcast: a searchable list of templates, each drawn the way the
 * customer will see it, with its own Send button.
 *
 * No "create template" here. The reference offers one; on this platform
 * templates are authored on the admin console, because a careless template
 * costs the whole WhatsApp Business Account its quality rating (F11b). A button
 * that opens a screen the person cannot use is worse than none.
 *
 * Only approved templates get a Send button. The others are listed — a client
 * looking for the one they submitted yesterday should find it and read why it
 * is not sendable yet — with the status in the button's place.
 */
export function TemplatesModal({ open, onClose, onPick, isPicking }: {
  open: boolean;
  onClose: () => void;
  onPick: (template: Template) => void;
  isPicking: boolean;
}) {
  const [q, setQ] = useState('');
  const [category, setCategory] = useState('');
  const [status, setStatus] = useState('');
  const templates = useQuery({ queryKey: ['templates'], queryFn: inboxApi.templates, enabled: open });

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const categories = useMemo(
    () => [...new Set((templates.data ?? []).map((t) => t.category).filter(Boolean) as string[])].sort(),
    [templates.data]);

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (templates.data ?? []).filter((t) =>
      (!needle || t.name.toLowerCase().includes(needle) || (t.bodyText ?? '').toLowerCase().includes(needle))
      && (!category || t.category === category)
      && (!status || (t.status ?? '').toUpperCase() === status));
  }, [templates.data, q, category, status]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-3" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Templates"
        onClick={(e) => e.stopPropagation()}
        className="flex max-h-[min(88vh,760px)] w-full max-w-2xl flex-col overflow-hidden rounded-(--radius-panel) bg-(--color-surface) shadow-(--shadow-pop)"
      >
        <header className="flex shrink-0 items-center justify-between gap-3 px-4 pt-4 pb-3">
          <h2 className="text-lead font-semibold">Templates</h2>
          <button type="button" onClick={onClose} aria-label="Close" className="tap grid size-9 place-items-center rounded-full text-(--color-muted) hover:bg-(--color-sunken)">
            <IconClose />
          </button>
        </header>

        <div className="shrink-0 px-4">
          <label className="flex h-10 items-center gap-2 rounded-(--radius-field) border border-(--color-line) px-3">
            <IconSearch />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search Template…"
              aria-label="Search templates"
              className="min-w-0 flex-1 bg-transparent text-body outline-none placeholder:text-(--color-faint)"
            />
          </label>
          <div className="mt-2 flex flex-wrap gap-2">
            <Filter value={status} onChange={setStatus} label="All status" options={['APPROVED', 'PENDING', 'REJECTED']} pretty={(v) => v.charAt(0) + v.slice(1).toLowerCase()} />
            <Filter value={category} onChange={setCategory} label="All categories" options={categories} pretty={(v) => v.charAt(0).toUpperCase() + v.slice(1).toLowerCase()} />
          </div>
        </div>

        <div className="pane-scroll mt-3 min-h-0 flex-1 overflow-y-auto border-t border-(--color-line)">
          {templates.isPending && <p className="px-4 py-8 text-center text-small text-(--color-muted)">Loading…</p>}
          {!templates.isPending && shown.length === 0 && (
            <p className="px-4 py-10 text-center text-small text-(--color-muted)">No template matches.</p>
          )}
          {shown.map((t) => {
            const approved = (t.status ?? '').toUpperCase() === 'APPROVED';
            const buttons = templateButtons(t);
            return (
              <section key={t.id} className="border-b border-(--color-line-dim) px-4 py-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="truncate text-body font-semibold">{t.name}</h3>
                    <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-small text-(--color-muted)">
                      {t.category && (
                        <span className="rounded-(--radius-chip) bg-(--color-sunken) px-2 py-0.5 text-small text-(--color-ink-soft)">
                          {t.category.charAt(0).toUpperCase() + t.category.slice(1).toLowerCase()}
                        </span>
                      )}
                      <span>{t.language}</span>
                      {t.syncedAt && <><span aria-hidden="true">•</span><span>{formatIst(t.syncedAt)}</span></>}
                    </div>
                  </div>
                  {approved ? (
                    <button
                      type="button"
                      disabled={isPicking}
                      onClick={() => onPick(t)}
                      className="tap flex shrink-0 items-center gap-2 rounded-(--radius-field) bg-(--color-brand) px-4 py-2 text-body font-semibold text-(--color-brand-ink) hover:bg-(--color-brand-strong) disabled:opacity-60"
                    >
                      <IconSend /> Send
                    </button>
                  ) : (
                    <span className="shrink-0 rounded-(--radius-chip) bg-(--color-note) px-2.5 py-1 text-small font-medium text-(--color-warn)">
                      {(t.status ?? 'unknown').charAt(0).toUpperCase() + (t.status ?? 'unknown').slice(1).toLowerCase()}
                    </span>
                  )}
                </div>

                {/* The preview: the message the way the customer sees it, on the
                    chat ground, because whether a template reads well is not
                    legible as a string. */}
                <div className="chat-ground mt-3 rounded-(--radius-field) p-3">
                  <div className="max-w-[min(20rem,100%)] overflow-hidden rounded-lg bg-(--color-bubble-in) text-(--color-bubble-ink) shadow-sm">
                    <div className="px-3 pt-2 pb-1.5">
                      {t.headerText && <p className="mb-1 text-body font-semibold">{t.headerText}</p>}
                      <p className="text-body whitespace-pre-wrap">{t.bodyText}</p>
                      {t.footerText && <p className="mt-1 text-small text-(--color-muted)">{t.footerText}</p>}
                    </div>
                    {buttons.length > 0 && (
                      <ul className="border-t border-(--color-line-dim)">
                        {buttons.map((b, i) => (
                          <li key={i} className={`py-1.5 text-center text-body font-medium text-(--color-tick-read) ${i > 0 ? 'border-t border-(--color-line-dim)' : ''}`}>
                            {b.text}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </section>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function Filter({ value, onChange, label, options, pretty }: {
  value: string; onChange: (v: string) => void; label: string; options: string[]; pretty: (v: string) => string;
}) {
  return (
    <label className="relative flex h-9 items-center gap-1 rounded-(--radius-field) border border-(--color-line) px-3 text-body font-medium text-(--color-ink-soft)">
      <span>{value ? pretty(value) : label}</span>
      <IconChevron />
      <select value={value} onChange={(e) => onChange(e.target.value)} aria-label={label} className="absolute inset-0 cursor-pointer opacity-0">
        <option value="">{label}</option>
        {options.map((o) => <option key={o} value={o}>{pretty(o)}</option>)}
      </select>
    </label>
  );
}

const s = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.6, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
const IconSearch = () => <svg viewBox="0 0 20 20" className="size-4.5 shrink-0 text-(--color-faint)" aria-hidden="true"><circle cx="8.8" cy="8.8" r="5.2" {...s} /><path d="m12.8 12.8 3.6 3.6" {...s} /></svg>;
const IconClose = () => <svg viewBox="0 0 20 20" className="size-5" aria-hidden="true"><path d="m5 5 10 10M15 5 5 15" {...s} /></svg>;
const IconChevron = () => <svg viewBox="0 0 20 20" className="size-4" aria-hidden="true"><path d="m6 8 4 4 4-4" {...s} /></svg>;
export const IconSend = () => <svg viewBox="0 0 20 20" className="size-4" aria-hidden="true"><path d="m3 3 14 7-14 7 2.5-7z" {...s} strokeLinejoin="round" /><path d="M5.5 10H17" {...s} /></svg>;
